import { API_URL } from "@/lib/api";
import { maskKey } from "@/lib/auth";

/**
 * The two things a student pastes to point the starter kit at one challenge.
 *
 * Everything challenge-independent -- installing uv, cloning the kit, what an agent is --
 * is the tutorial on the front page; the API is the SDK reference. What belongs here is
 * exactly what differs per challenge and per student: this challenge's id, the reader's
 * own key when signed in, and this deployment's API URL when it is not the one the SDK
 * assumes. The key goes in a file git ignores, never in code they might commit.
 */

/** What the SDK connects to when `VIS_NAV_SERVER` is unset; a dev server needs the line. */
export const SDK_DEFAULT_SERVER = "https://visual-navigation-challenge-api.ai4ce.dev";

/** The kit's run command; `my_agent.py` is the file the student fills in. */
export const RUN_COMMAND = "uv run source/my_agent.py";

/** The contents of `vis_nav_player/.env` for this challenge. `mask` gives the on-screen
 * form; the copied form carries the real key. */
export function dotenv(challengeId: string, apiKey: string | null, { mask = false } = {}): string {
  const key = apiKey === null ? "<your key>" : mask ? maskKey(apiKey) : apiKey;
  const lines = [`VIS_NAV_API_KEY=${key}`, `VIS_NAV_CHALLENGE=${challengeId}`];
  if (API_URL !== SDK_DEFAULT_SERVER) lines.push(`VIS_NAV_SERVER=${API_URL}`);
  return lines.join("\n");
}

export type Shell = "sh" | "powershell";

/** One paste that writes the file: a heredoc in a POSIX shell, a here-string in PowerShell.
 * Quoted, so a key with `$` in it is written as typed. */
export function writeDotenv(contents: string, shell: Shell): string {
  return shell === "powershell"
    ? `@'\n${contents}\n'@ | Set-Content .env`
    : `cat > .env <<'EOF'\n${contents}\nEOF`;
}

/** The shell this reader most likely has open, from the browser's platform string. */
export function likelyShell(): Shell {
  return typeof navigator !== "undefined" && /win/i.test(navigator.platform) ? "powershell" : "sh";
}
